import React, { useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, BookOpen, Users, Globe, Award, Sparkles, Target, Lightbulb, Check, GraduationCap } from "lucide-react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Program Logos
const PROGRAM_LOGOS = {
  future: "/assets/images/logos/future.png"
};

// Program data with FULL content exactly as provided
const PROGRAMS_DATA = {
  future: {
    name: "FUTURE",
    fullName: "Faculty Upskilling Through Unlearning, and Relearning for Excellence",
    tagline: "Because when educators evolve, education itself advances.",
    description: [
      "FUTURE is GSLC's transformative initiative focused on reimagining professional growth for educators in a rapidly evolving world.",
      "At its core, FUTURE recognises a powerful truth: excellence in education is not sustained by what we know, but by our willingness to rethink what we know. In an era shaped by artificial intelligence, changing learner profiles, interdisciplinary thinking, and global benchmarks, educators must continuously evolve — not just by adding new skills, but by consciously unlearning outdated practices and relearning with renewed clarity.",
      "FUTURE is designed to support faculty in this journey of intellectual renewal and professional reinvention. Through curated learning experiences, reflective engagements, strategic workshops, and exposure to emerging pedagogical frameworks."
    ],
    features: [
      "Adapt to AI-enabled and technology-integrated classrooms",
      "Shift from content delivery to learning design",
      "Foster critical thinking, creativity, and problem-solving",
      "Lead with confidence in dynamic academic environments",
      "Build future-ready institutional cultures"
    ],
    featuresCenter: true,
    conclusion: "FUTURE is not merely a training program. It is a mindset movement. It encourages educators to question assumptions, embrace adaptability, and cultivate lifelong learning — ensuring that schools remain relevant, resilient, and exceptional.",
    icon: Lightbulb,
    color: "yellow",
    logo: PROGRAM_LOGOS.future
  },
  "leadership-ascendancy": {
    name: "Leadership Ascendancy",
    fullName: "The Leadership Ascendancy Program: Redefining School Leadership for Complex Times",
    tagline: "From administrators to architects of institutional futures.",
    description: [
      "The Leadership Ascendancy Program is GSLC's flagship executive leadership initiative designed for visionary principals, trustees, and school owners navigating an era of systemic disruption and accelerated change.",
      "Conceptualised as a high-impact, intellectually rigorous engagement, the program bridges global strategic thinking with the realities of Indian school ecosystems.",
      "In collaboration with internationally renowned faculty such as Marc Ventresca, Associate Professor of Strategic Management at Oxford Saïd Business School, the program brings world-class executive education into the school leadership domain."
    ],
    philosophy: {
      intro: "Today's school leaders operate in:",
      points: [
        "Complex and contested environments",
        "Rapid technological transformation",
        "Shifting governance expectations",
        "Emerging educational markets",
        "Increasing stakeholder scrutiny"
      ],
      conclusion: "The Leadership Ascendancy Program does not offer surface-level training. It equips leaders with frameworks to interpret complexity, design strategy, and govern responsibly."
    },
    pillars: [
      { title: "Leading in Complex, Contested Contexts", desc: "Helping principals understand the architecture of institutional chaos — and lead with clarity." },
      { title: "Systems Change at Scale", desc: "Enabling school owners to think beyond campuses — toward networks, ecosystems, and sustainable growth." },
      { title: "Digital & AI-Enabled Transformation and Governance", desc: "Shifting the narrative from AI anxiety to strategic stewardship." },
      { title: "Theories of Organisations & Institutions", desc: "Understanding why reforms fail — and how resilient institutions are built." },
      { title: "Strategy in Emerging Educational Markets", desc: "Equipping edupreneurs to navigate expansion, differentiation, and long-term positioning." }
    ],
    audience: ["Progressive School Principals", "Trustees & Promoters", "Education Entrepreneurs", "Senior Academic Leaders"],
    format: ["Curated executive sessions (global faculty)", "Case-based learning", "Strategic reflection labs", "Cohort-based peer dialogue", "Institutional blueprint development"],
    outcome: [
      "Participants will not simply acquire knowledge — they will gain strategic perspective, governance confidence, and institutional foresight.",
      "The Leadership Ascendancy Program is designed to move leaders:",
      "From operational to strategic",
      "From reactive to systemic",
      "From administrators to architects of institutional futures"
    ],
    icon: Target,
    color: "red"
  },
  "global-ascend": {
    name: "Global Ascend",
    fullName: "International Leadership Immersion Initiative",
    tagline: "To shape the future of Indian education, leaders must first experience the world.",
    description: [
      "GSLC Global Ascend Program is an international leadership immersion initiative designed to expand the global perspective of progressive school leaders.",
      "The program curates high-impact cohorts of principals, trustees, and education entrepreneurs and takes them to some of the world's most forward-thinking education ecosystems including Finland, Australia, Singapore, the United Kingdom, and the United States.",
      "More than a study tour, Global Ascend is a structured learning journey."
    ],
    learnings: [
      "Curriculum design and assessment models",
      "Leadership and governance frameworks",
      "Technology integration and AI adoption",
      "Teacher development systems",
      "Student wellbeing and holistic education models",
      "Institutional strategy and sustainability"
    ],
    components: [
      "Curated school visits",
      "University immersions",
      "Leadership workshops",
      "Certified executive sessions with international partners"
    ],
    transformation: [
      "From local benchmarking to global thinking",
      "From adaptation to innovation",
      "From operational management to systemic leadership"
    ],
    finalNote: [
      "Participants return not only with exposure but with certification, strategic insight, and an institutional action blueprint tailored to their own schools.",
      "GSLC Global Ascend is built on a simple belief:"
    ],
    conclusion: "It is not about travel. It is about transformation.",
    icon: Globe,
    color: "blue"
  }
};

// Individual Program Page Component
const ProgramDetail = ({ programId }) => {
  const program = PROGRAMS_DATA[programId];
  
  if (!program) {
    return (
      <div className="pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-heading text-slate-800 mb-4">Service Not Found</h1>
          <Link to="/programs" className="btn-gradient">View All Services</Link>
        </div>
      </div>
    );
  }

  const IconComponent = program.icon;
  const colorClasses = {
    yellow: "from-yellow-100 to-yellow-200 text-yellow-600",
    red: "from-red-100 to-red-200 text-red-600",
    blue: "from-blue-100 to-blue-200 text-blue-600"
  };

  return (
    <div className="pt-24">
      {/* Hero */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-yellow-50 via-white to-blue-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            {program.logo ? (
              <img src={program.logo} alt={program.name} className="h-44 md:h-52 mx-auto mb-6 object-contain rounded-xl" />
            ) : (
              <div className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${colorClasses[program.color]} flex items-center justify-center mx-auto mb-6`}>
                <IconComponent className="w-12 h-12" />
              </div>
            )}
            <h1 className="font-heading text-4xl md:text-6xl font-semibold mb-4 text-slate-800">
              {program.name}
            </h1>
            <p className="font-accent text-red-600 text-base md:text-lg tracking-wide mb-6">{program.fullName}</p>
            <p className="text-xl text-slate-600 italic max-w-2xl mx-auto">
              "{program.tagline}"
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Full Description */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              {program.description.map((para, idx) => (
                <p key={idx} className="text-slate-600 leading-relaxed mb-6 text-lg">{para}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Philosophy Section for Leadership Ascendancy */}
      {program.philosophy && (
        <AnimatedSection className="section-padding bg-gradient-to-r from-slate-50 via-white to-slate-50">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="max-w-4xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-800 mb-6 text-center">
                Program <span className="gradient-text">Philosophy</span>
              </h2>
              <div className="card-light rounded-2xl p-8">
                <p className="text-slate-700 text-lg mb-4">{program.philosophy.intro}</p>
                <ul className="space-y-3 mb-6">
                  {program.philosophy.points.map((point, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-600">
                      <Check className="w-5 h-5 text-red-500 flex-shrink-0" />
                      {point}
                    </li>
                  ))}
                </ul>
                <p className="text-slate-700 text-lg font-medium">{program.philosophy.conclusion}</p>
              </div>
            </motion.div>
          </div>
        </AnimatedSection>
      )}

      {/* Features for FUTURE */}
      {program.features && (
        <AnimatedSection className="section-padding bg-gradient-to-br from-blue-50 via-white to-yellow-50">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-800">
                The Initiative Empowers Educators To
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {program.features.map((item, idx) => (
                <motion.div key={idx} variants={fadeInUp} className="card-light rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-red-500 flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold">{idx + 1}</span>
                  </div>
                  <p className="text-slate-700">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* Pillars for Leadership Ascendancy */}
      {program.pillars && (
        <AnimatedSection className="section-padding bg-gradient-to-br from-blue-50 via-white to-yellow-50">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-800">
                Core Learning <span className="gradient-text">Pillars</span>
              </h2>
            </motion.div>
            <div className="space-y-6 max-w-4xl mx-auto">
              {program.pillars.map((pillar, idx) => (
                <motion.div key={idx} variants={fadeInUp} className="card-light rounded-2xl p-6">
                  <h3 className="font-heading text-xl text-slate-800 mb-2">{idx + 1}. {pillar.title}</h3>
                  <p className="text-slate-600">{pillar.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* Audience & Format for Leadership Ascendancy */}
      {program.audience && program.format && (
        <AnimatedSection className="section-padding">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Who It Is For */}
              <motion.div variants={fadeInUp} className="card-light rounded-2xl p-8">
                <h3 className="font-heading text-2xl text-slate-800 mb-6">Who It Is For</h3>
                <ul className="space-y-3">
                  {program.audience.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-600">
                      <Users className="w-5 h-5 text-red-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Program Format */}
              <motion.div variants={fadeInUp} className="card-light rounded-2xl p-8">
                <h3 className="font-heading text-2xl text-slate-800 mb-6">Program Format</h3>
                <ul className="space-y-3">
                  {program.format.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-slate-600">
                      <Check className="w-5 h-5 text-yellow-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* Learnings & Components for Global Ascend */}
      {program.learnings && (
        <AnimatedSection className="section-padding bg-gradient-to-br from-blue-50 via-white to-yellow-50">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-800">
                Participants Engage Directly With
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {program.learnings.map((item, idx) => (
                <motion.div key={idx} variants={fadeInUp} className="card-light rounded-xl p-6 flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-slate-700">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* Components for Global Ascend */}
      {program.components && (
        <AnimatedSection className="section-padding">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-800">
                Program <span className="gradient-text">Components</span>
              </h2>
            </motion.div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {program.components.map((item, idx) => (
                <motion.div key={idx} variants={fadeInUp} className="card-light rounded-xl p-6 text-center">
                  <p className="text-slate-700 font-medium">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* Transformation for Global Ascend */}
      {program.transformation && (
        <AnimatedSection className="section-padding bg-gradient-to-r from-slate-50 via-white to-slate-50">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-800 mb-8">
                Each Cohort Experience Moves Leaders
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {program.transformation.map((item, idx) => (
                  <motion.div key={idx} variants={fadeInUp} className="card-light rounded-xl p-6">
                    <p className="text-slate-700 font-heading text-lg">{item}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </AnimatedSection>
      )}

      {/* Outcome for Leadership Ascendancy */}
      {program.outcome && (
        <AnimatedSection className="section-padding bg-gradient-to-r from-slate-50 via-white to-slate-50">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="max-w-4xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-slate-800 mb-8 text-center">
                The <span className="gradient-text">Outcome</span>
              </h2>
              <div className="card-light rounded-2xl p-8">
                {program.outcome.map((item, idx) => (
                  <p key={idx} className="text-slate-700 text-lg mb-4 last:mb-0">{item}</p>
                ))}
              </div>
            </motion.div>
          </div>
        </AnimatedSection>
      )}

      {/* Final Note for Global Ascend */}
      {program.finalNote && (
        <AnimatedSection className="section-padding">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="max-w-3xl mx-auto text-center">
              {program.finalNote.map((item, idx) => (
                <p key={idx} className="text-slate-600 text-lg mb-4">{item}</p>
              ))}
            </motion.div>
          </div>
        </AnimatedSection>
      )}

      {/* Conclusion */}
      {program.conclusion && (
        <AnimatedSection className="section-padding bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900">
          <div className="container-custom">
            <motion.div variants={fadeInUp} className="max-w-3xl mx-auto text-center">
              <p className="text-xl text-white leading-relaxed italic">
                {program.conclusion}
              </p>
            </motion.div>
          </div>
        </AnimatedSection>
      )}

      {/* CTA */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="text-center">
            <Link to="/contact" className="btn-gradient inline-flex items-center gap-2">
              Inquire About This Service <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>
  );
};

// Programs Overview Page - Renamed to "Our Other Services"
const ProgramsOverview = () => {
  const programs = Object.entries(PROGRAMS_DATA).map(([id, data]) => ({ id, ...data }));

  return (
    <div className="pt-24">
      {/* Hero */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-yellow-50 via-white to-blue-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Leadership Development</span>
            <h1 className="font-heading text-4xl md:text-6xl font-semibold mt-4 mb-6 text-slate-800">
              <span className="gradient-text">Other Services</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Transformative initiatives designed to empower educators, school leaders, and institutions 
              to navigate the evolving landscape of education with confidence and excellence.
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Programs Grid */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            {programs.map((program) => {
              const IconComponent = program.icon;
              const colorClasses = {
                yellow: "from-yellow-100 to-yellow-200 text-yellow-600",
                red: "from-red-100 to-red-200 text-red-600",
                blue: "from-blue-100 to-blue-200 text-blue-600"
              };

              return (
                <motion.div key={program.id} variants={fadeInUp}>
                  <Link 
                    to={`/programs/${program.id}`}
                    className="block card-light rounded-2xl p-8 hover-lift h-full"
                  >
                    {program.logo ? (
                      <img src={program.logo} alt={program.name} className="h-28 mb-6 object-contain rounded-lg" />
                    ) : (
                      <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${colorClasses[program.color]} flex items-center justify-center mb-6`}>
                        <IconComponent className="w-10 h-10" />
                      </div>
                    )}
                    <h3 className="font-heading text-2xl text-slate-800 mb-2">{program.name}</h3>
                    <p className="text-sm text-red-600 font-accent mb-4">{program.fullName}</p>
                    <p className="text-slate-600 text-sm line-clamp-3 mb-4">
                      {program.description[0]}
                    </p>
                    <span className="text-blue-700 font-medium inline-flex items-center gap-2 group">
                      Learn More 
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </AnimatedSection>
    </div>
  );
};

// Main Programs Page Component
const ProgramsPage = () => {
  const { programId } = useParams();
  
  if (programId) {
    return <ProgramDetail programId={programId} />;
  }
  
  return <ProgramsOverview />;
};

export default ProgramsPage;
